import React, {useEffect, useMemo, useState, useCallback, useRef} from "react";
import {createSearchParams, useNavigate} from "react-router-dom";
import api, {API_PATHS, AuthService} from "../service/api";
import {useAppDispatch} from "../store/hooks";
import {initiateNexusLandingDataExtraction} from "../store/ganttChart/action";
import {
    setActiveNexusFilter,
    setSelectedNexusFilterDate,
} from "../store/nexusFilters";
import AppInitializationService from "../service/AppInitializationService";
import labels from "../utils/labels";
import {AxiosResponse} from "axios";
import MediumHeader from "../components/UI/textFields/MediumHeader";
import StandardButton from "../components/UI/buttons/StandardButton";
import {Spinner} from "../components/UI/Spinner";

type NexusFilter = {
    id: number;
    idResponsibleUser: number;
    idRuleset: number;
    filter: string;
    filterId: number;
    activatedDateTime: string;
    active: boolean;
    startTime: string;
    endTime: string;
};

/**
 * Response from the "create filter if it doesn't exist" endpoint. We make sure to check if data exists for the
 * planning date in question if the filter exists.
 */
type NexusFilterResponse = {
    filter: NexusFilter;
    error?: string;
    dataAlreadyExists?: boolean;
};

const AlreadyExistsWarning = ({
                                  onGoToExistingClick,
                                  onOverwriteExistingClick,
                              }: {
    onGoToExistingClick: () => void;
    onOverwriteExistingClick: () => Promise<void>;
}) => {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 h-[190px] w-[620px]">
                <MediumHeader classes={"pb-2"}>
                    {labels.nexusLandingDataAlreadyExistsWarningHeader}
                </MediumHeader>
                <p className={"bodyTwo pb-2"}>
                    {labels.nexusLandingDataAlreadyExistsWarningBody}
                </p>
                <div className={"flex flex-row justify-end"}>
                    <StandardButton
                        variant={"SECONDARY"}
                        classes={"mr-6"}
                        onClick={onGoToExistingClick}
                    >
                        {labels.nexusLandingPageGoToExistingButton}
                    </StandardButton>
                    <StandardButton
                        variant={"PRIMARY"}
                        onClick={onOverwriteExistingClick}
                    >
                        {labels.nexusLandingPageOverwriteData}
                    </StandardButton>
                </div>
            </div>
        </div>
    );
};

const NexusLandingPage = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    // Prevent multiple executions of the OAuth flow
    const hasInitialized = useRef(false);

    const [pageDisplayState, setPageDisplayState] = useState<
        "Spinner" | "Error" | "AlreadyExistsWarning"
    >("Spinner");

    const [pendingNexusFilterResponse, setPendingNexusFilterResponse] = useState<
        AxiosResponse<NexusFilterResponse> | undefined
    >();

    const appInitializationService = useMemo(() => {
        return new AppInitializationService(dispatch);
    }, [dispatch]);

    const hasPassedAuthChallenge = (): boolean => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.has("code");
    };

    const handleInitialLanding = async () => {
        const searchParams = new URLSearchParams(window.location.search);

        // Check if we have URL params (initial landing before auth)
        const filterId = searchParams.get("filterId");
        const date = searchParams.get("date");
        const filterName = searchParams.get("filterName");
        const resourceIds = searchParams.get("resourceIds");

        if (filterId && date && filterName) {
            // Initial landing - perform OAuth flow
            await AuthService.performLogin(filterId, date, filterName, resourceIds);
            return;
        }

        // Otherwise, this is post-auth redirect - state param should exist
        const stateData = decodeStateParam();

        if (!stateData || !stateData.filterId || !stateData.date || !stateData.filterName) {
            console.error("Missing required parameters after auth redirect");
            navigate("/");
            return;
        }

        // stateData is available for use in subsequent steps
    };

    const sanitizeLocalDate = (date: string): string => date.split("T")[0];

    /**
     * Decodes the "state" parameter from the URL.
     * @returns {object|null} The decoded state object or null if decoding fails.
     */
    const decodeStateParam = (): {
        date: string | null;
        filterId: string | null;
        filterName: string | null;
        resourceIds: string | null;
    } | null => {
        const searchParams = new URLSearchParams(window.location.search);
        const stateParam = searchParams.get("state");

        if (!stateParam) {
            return null;
        }

        try {
            const decoded = atob(stateParam);
            return JSON.parse(decoded);
        } catch (error) {
            console.error("Failed to decode state parameter:", error);
            return null;
        }
    };

    const startNexusLandingDataExtraction = async (
        nexusFilterResponse: AxiosResponse<NexusFilterResponse>,
    ) => {
        // Decode state parameter to get the original query data
        const stateData = decodeStateParam();

        if (!stateData) {
            console.error("Cannot extract data without state parameters");
            setPageDisplayState("Error");
            return;
        }

        const { filterId, date, filterName, resourceIds } = stateData;

        const sanitizedFilterName = decodeURIComponent(filterName ?? "");
        const sanitizedDate = date!.split("T")[0];
        const sanitizedResourceIds = decodeURIComponent(resourceIds ?? "");

        // Set active filter and date before redirecting
        dispatch(
            setActiveNexusFilter({
                ...nexusFilterResponse.data.filter,
                filterName: sanitizedFilterName,
            }),
        );
        dispatch(setSelectedNexusFilterDate(sanitizedDate));
        // We have a filter, queue the data extraction
        const data = nexusFilterResponse.data.filter as NexusFilter;

        dispatch(
            initiateNexusLandingDataExtraction({
                filterId: parseInt(filterId!),
                date: sanitizeLocalDate(date!),
                pondooFilterId: data.id,
                filterName: sanitizedFilterName,
                resourceIds: sanitizedResourceIds,
            }),
        );

        setTimeout(() => {
            navigate({
                pathname: "/",
                search: createSearchParams({
                    filter: data.id.toString(),
                    date: sanitizedDate,
                }).toString(),
            });
        }, 2000);
    };

    /**
     * Performs an initial hand-shake with the Pondoo application to ensure we have a filter ready. Also checks
     * if the filter already has data on the given planning date - if so, the response is stashed and the user is informed.
     */
    const initiateNexusLandingChecks = async () => {
        // Decode state parameter to get the original query data
        const stateData = decodeStateParam();

        if (!stateData) {
            console.error("No state parameter found after auth redirect");
            setPageDisplayState("Error");
            return;
        }

        const { filterId, date, filterName } = stateData;

        const sanitizedFilterName = decodeURIComponent(filterName ?? "");
        const sanitizedDate = date!.split("T")[0];

        // Ensure we can create the Nexus filter
        const nexusFilterResponse = await api.post<NexusFilterResponse>(
            API_PATHS.createOrUpdateNexusFilter,
            {
                filterId: filterId,
                shouldDisable: false,
                displayName: sanitizedFilterName,
                date: sanitizedDate,
            },
        );

        if (!(nexusFilterResponse.status === 200)) {
            setPageDisplayState("Error");
            return;
        }

        if (nexusFilterResponse.data.dataAlreadyExists) {
            setPendingNexusFilterResponse(nexusFilterResponse);
            setPageDisplayState("AlreadyExistsWarning");
            return;
        }
        await startNexusLandingDataExtraction(nexusFilterResponse);
    };

    const handleSuccessfulAuthCheckLanding = async () => {
        const tokens = await AuthService.exchangeCodeForToken();

        if (!tokens) {
            console.error("Could not authenticate with tokens");
            navigate("/notfound");
            return;
        }


        appInitializationService.configureAppInitialization(
            tokens.accessToken,
            tokens.refreshToken,
        );

        await initiateNexusLandingChecks();
    };

    useEffect(() => {
        // Prevent multiple executions - OAuth codes can only be used once
        if (hasInitialized.current) {
            return;
        }

        hasInitialized.current = true;

        if (hasPassedAuthChallenge()) {
            handleSuccessfulAuthCheckLanding();
        } else {
            handleInitialLanding();
        }
        // Empty dependency array - only run once on mount
    }, []);

    return (
        <div
            className="flex items-center justify-center"
            style={{width: "100%", height: "100%"}}
        >
            {{
                Spinner: <Spinner/>,
                Error: <h1>{labels.pondooNexusLandingError}</h1>,
                AlreadyExistsWarning: (
                    <AlreadyExistsWarning
                        onGoToExistingClick={() => {
                            const data = pendingNexusFilterResponse!.data;
                            const stateData = decodeStateParam();
                            const sanitizedDate = stateData?.date?.split("T")[0];
                            navigate({
                                pathname: "/",
                                search: createSearchParams({
                                    filter: data.filter.id.toString(),
                                    date: sanitizedDate!,
                                }).toString(),
                            });
                        }}
                        onOverwriteExistingClick={async () => {
                            await startNexusLandingDataExtraction(
                                pendingNexusFilterResponse!,
                            );
                        }}
                    />
                ),
            }[pageDisplayState] || <Spinner/>}
        </div>
    );
};

export default NexusLandingPage;
